import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  EuiInMemoryTable,
  EuiBadgeGroup,
  EuiBadge,
  EuiBasicTableColumn,
  EuiLink,
  EuiText,
  EuiCode,
  EuiSwitch,
  EuiSpacer,
  EuiFlexGroup,
  EuiFlexItem,
} from "@elastic/eui";
import {
  AnketaActionsEnum,
  AnketaResourcesEnum,
  KeycloakOrganizations,
  useAnketaI18n,
  useApi,
  useToast,
} from "../..";
import { useNavigate, useParams } from "react-router";
import { AnketaCatalogs, AnketaQuestionnaire } from "../types";
import { useAnketaAuth } from "../../auth/context";

/**
 * Extract a human-renderable message from a failed AnketaResponse (or a thrown
 * value). The backend detail lives in `error.details` (string | string[]);
 * fall back to `error.message`, then a generic string. Never returns an object,
 * so it is always safe as a React child.
 */
const renderApiError = (response: any): string => {
  const err = response?.error ?? response;
  const detail = err?.details ?? err?.message ?? err;
  if (Array.isArray(detail)) return detail.filter(Boolean).join(", ");
  if (typeof detail === "string") return detail;
  if (detail instanceof Error) return detail.message;
  const status = response?.status ?? err?.status;
  return status ? `Request failed (${status})` : "Request failed";
};

export const AnketaQuestionnaireTable = ({
  catalogs,
  setRetrigger,
}: {
  catalogs: Array<AnketaCatalogs>;
  setRetrigger: Dispatch<SetStateAction<number>>;
}): JSX.Element => {
  const navigate = useNavigate();
  const { catalogId } = useParams();
  const { translate } = useAnketaI18n();
  const { Delete, Put } = useApi();
  const { keycloak, checkPermission } = useAnketaAuth();
  const { addToast } = useToast();
  const [showArchived, setShowArchived] = useState<boolean>(false);

  // Destructive ops mirror the server: delete·all / archive·all = admin AND
  // isAutonubil (AUTH_RECOMMENDATION.md §5). UI affordance only.
  const canDelete = checkPermission(
    AnketaResourcesEnum.questionnaires,
    AnketaActionsEnum.delete,
    { isAutonubil: true },
  );
  const canArchive = checkPermission(
    AnketaResourcesEnum.questionnaires,
    AnketaActionsEnum.archive,
    { isAutonubil: true },
  );

  const setArchived = async (item: AnketaQuestionnaire, archived: boolean) => {
    // useApi never rejects: Put resolves an AnketaResponse { success, error }
    // even on 403/500. Branching on `response.success` (not .then/.catch) is the
    // only way to tell an archive that worked from one that silently failed -
    // otherwise the user always sees a "success" toast regardless of the outcome.
    const response = await Put({
      path: `v2/${AnketaResourcesEnum.questionnaires}/${catalog.id}/${item.id}/archive`,
      value: { archived },
      responseType: "json",
      token: keycloak.token,
    }).catch((err) => err);

    if (response?.success) {
      addToast({
        color: "success",
        iconType: archived ? "folderClosed" : "folderOpen",
        title: translate(
          archived
            ? "anketa.table.entry.archive.success.header"
            : "anketa.table.entry.restore.success.header",
        ),
        text: translate(
          archived
            ? "anketa.table.entry.archive.success.body"
            : "anketa.table.entry.restore.success.body",
        ),
      });
      setRetrigger((prev) => prev + 1);
      return;
    }

    addToast({
      color: "danger",
      iconType: "alert",
      title: translate("anketa.table.entry.archive.error.header"),
      text: <EuiCode>{renderApiError(response)}</EuiCode>,
    });
  };

  const catalog = useMemo<AnketaCatalogs>(() => {
    if (catalogs) {
      const questionnaires = catalogs.find(
        (catalog) => catalog.id === catalogId,
      );
      if (questionnaires && questionnaires.questionnaire) {
        return questionnaires;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }, [catalogId, catalogs]);

  const columns: Array<EuiBasicTableColumn<AnketaQuestionnaire>> = [
    {
      field: "displayName",
      name: translate("anketa.displayName"),
      sortable: true,
      truncateText: true,
      render: (name: string, questionnaire: AnketaQuestionnaire) => (
        <EuiBadge iconType={"copy"}>
          <EuiLink
            onClick={() => {
              navigate(
                `/${AnketaResourcesEnum.questionnaires}/${catalog.id}/${questionnaire.id}`,
              );
            }}
          >
            {name}
          </EuiLink>
        </EuiBadge>
      ),
    },
    {
      field: "questionnaireCatalog",
      name: `${translate("anketa.questionnaire")} ID`,
      sortable: false,
      render: (id: string) => <EuiText size="s">{catalog.displayName}</EuiText>,
    },
    {
      field: "organization",
      name: translate("anketa.organization"),
      sortable: true,
      render: (orgs: KeycloakOrganizations[]) => {
        if (orgs && orgs.length > 0 && orgs !== null) {
          const uniqueOrgs = orgs.filter(
            (org, index, self) =>
              self.findIndex(
                (o) => o !== null && org !== null && o.id === org.id,
              ) === index,
          );
          return (
            <EuiBadgeGroup gutterSize="s">
              {uniqueOrgs.filter(Boolean).map((org) => (
                <EuiBadge key={org.id}>{org.name}</EuiBadge>
              ))}
            </EuiBadgeGroup>
          );
        }
      },
    },
    {
      field: "members",
      name: translate("anketa.members"),
      sortable: true,
      render: (members: string[]) => (
        <EuiBadgeGroup gutterSize="xs">
          {members.map((email) => (
            <EuiBadge iconType="email" key={email}>
              {email}
            </EuiBadge>
          ))}
        </EuiBadgeGroup>
      ),
    },
    {
      actions: [
        {
          name: translate("anketa.table.details.share"),
          description: translate("anketa.table.details.share"),
          icon: "copy",
          type: "icon",
          onClick: (item: AnketaQuestionnaire) => {
            const url = `${window.location.origin}/${AnketaResourcesEnum.questionnaires}/${catalog.id}/${item.id}`;
            navigator.clipboard.writeText(url);
          },
        },
        {
          name: translate("anketa.table.details"),
          description: translate("anketa.table.details"),
          icon: "eye",
          type: "icon",
          onClick: (item: AnketaQuestionnaire) => {
            navigate(
              `/${AnketaResourcesEnum.questionnaires}/${catalog.id}/${item.id}`,
            );
          },
        },
        {
          name: translate("anketa.table.details.archive"),
          description: translate("anketa.table.details.archive"),
          icon: "folderClosed",
          type: "icon",
          available: (item: AnketaQuestionnaire) => canArchive && !item.archived,
          onClick: (item: AnketaQuestionnaire) => setArchived(item, true),
        },
        {
          name: translate("anketa.table.details.restore"),
          description: translate("anketa.table.details.restore"),
          icon: "folderOpen",
          type: "icon",
          available: (item: AnketaQuestionnaire) => canArchive && !!item.archived,
          onClick: (item: AnketaQuestionnaire) => setArchived(item, false),
        },
        {
          name: translate("anketa.table.details.delete"),
          description: translate("anketa.table.details.delete"),
          icon: "trash",
          color: "danger",
          type: "icon",
          available: () => canDelete,
          onClick: async (item: AnketaQuestionnaire) => {
            // Same as archive: Delete resolves an AnketaResponse and never
            // rejects, so branch on response.success rather than .then/.catch.
            const response = await Delete<{ id: string; catalogId: string }>({
              path: `v2/${AnketaResourcesEnum.questionnaires}/${catalog.id}/${item.id}`,
              responseType: "json",
              token: keycloak.token,
            }).catch((err) => err);

            if (response?.success) {
              addToast({
                color: "success",
                iconType: "trash",
                title: translate("anketa.table.entry.delete.success.header"),
                text: translate("anketa.table.entry.delete.success.body"),
              });
              setRetrigger((prev) => prev + 1);
              return;
            }

            addToast({
              color: "danger",
              iconType: "alert",
              title: translate("anketa.table.entry.delete.error.header"),
              text: <EuiCode>{renderApiError(response)}</EuiCode>,
            });
          },
        },
      ],
    },
  ];

  useEffect(() => {
    if (
      catalog &&
      catalog !== null &&
      catalog.questionnaire &&
      catalog.questionnaire.length === 1
    ) {
      navigate(
        `/${AnketaResourcesEnum.questionnaires}/${catalogs[0].id}/${catalog.questionnaire[0].id}`,
      );
    }
  }, [catalog]);

  const items = useMemo<Array<AnketaQuestionnaire>>(() => {
    const all = catalog !== null ? catalog.questionnaire : [];
    return showArchived ? all : all.filter((q) => !q.archived);
  }, [catalog, showArchived]);

  const archivedCount = useMemo<number>(() => {
    const all = catalog !== null ? catalog.questionnaire : [];
    return all.filter((q) => q.archived).length;
  }, [catalog]);

  return (
    <>
      <EuiFlexGroup justifyContent="flexEnd" alignItems="center">
        <EuiFlexItem grow={false}>
          <EuiSwitch
            label={translate("anketa.table.showArchived")}
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            disabled={archivedCount === 0}
          />
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size="s" />
      <EuiInMemoryTable
        items={items}
        columns={columns}
        pagination={true}
        search
        sorting
      />
    </>
  );
};
